import { isAdvancedAvType } from '@/utils/age_verification';
import { appStore } from '@/stores/app';
import {
    GA_EVENT_NAMES,
    SearchSuggestionType,
    GifGAEvent,
    SendVideoClickParams,
    ChannelGAEvent,
    PhotoGAEvent,
    TrackingIdEvents,
    GAEvent,
    SignupType,
} from './types';

const sendGAEvent = (event: GAEvent) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(event);
};

/**
 * Tracks the user's country code.
 */
export const sendGeoEvent = (countryCode: string) => {
    sendGAEvent({
        event_name: GA_EVENT_NAMES.GEO,
        country_code: countryCode,
    });
};

/**
 * Tracks the selection of a search suggestion.
 * A suggestion can be selected by keyboard and by clicking.
 */
export const sendSearchSuggestionSelectEvent = (type: SearchSuggestionType, keyword: string) => {
    sendGAEvent({
        event: GA_EVENT_NAMES.GENERIC,
        event_name: GA_EVENT_NAMES.SEARCH_SUGGESTION_SELECTED,
        search_suggestion_type: type,
        search_suggestion_value: keyword,
    });
};

/**
 * Tracks when search is made without selecting a recommendation.
 */
export const sendGenericSearchEvent = (keyword: string) => {
    sendGAEvent({
        event: GA_EVENT_NAMES.GENERIC,
        event_name: GA_EVENT_NAMES.SEARCH,
        search_term: keyword,
    });
};

export const sendSearchPageVideoClick = (params: SendVideoClickParams) => {
    sendGAEvent({
        event: GA_EVENT_NAMES.GENERIC,
        event_name: GA_EVENT_NAMES.SEARCH_TO_VIDEO_CLICK,
        video_id: String(params.videoId),
        page_number: String(params.pageNumber),
        item_position: String(params.itemPosition),
        keyword: params.keyword,
        country_code: params.countryCode,
        orientation: params.orientation,
        logged_in: String(params.loggedIn),
    });
};

/**
 * Tracks when the layout change icon is clicked.
 */
export const sendLayoutChangeClickEvent = (fromTo: string) => {
    sendGAEvent({
        event: GA_EVENT_NAMES.GENERIC,
        event_name: GA_EVENT_NAMES.CLICK_LAYOUT_CHANGE_ICON,
        from_to: fromTo,
    });
};

/**
 * Tracks when a user has registered on the site.
 */
export const sendSignupEvent = (
    trId: string | null = null,
    isAV: boolean | null = null,
    signupType: SignupType | null = null
) => {
    const eventData: GAEvent = {
        event: GA_EVENT_NAMES.GENERIC,
        event_name: GA_EVENT_NAMES.SIGNUP,
    };

    if (trId !== null) {
        eventData.user_id = trId;
    }

    if (isAV !== null) {
        eventData.is_av = isAV ? 'true' : 'false';
    }

    if (signupType !== null) {
        eventData.signup_type = signupType;
    }

    sendGAEvent(eventData);
};

/**
 * Tracks when a user has logged in to the site.
 */
export const sendLoginEvent = (trId: string) => {
    sendGAEvent({
        event: GA_EVENT_NAMES.GENERIC,
        event_name: GA_EVENT_NAMES.LOGIN,
        user_id: trId,
    });
};

/**
 * Tracks when age verification is completed.
 */
export const sendAgeVerificationCompletedEvent = () => {
    sendGAEvent({
        event: GA_EVENT_NAMES.GENERIC,
        event_name: GA_EVENT_NAMES.AGE_VERIFICATION_COMPLETED,
    });
};

/**
 * Tracks various GIF page events, such as button clicks.
 */
export const sendGifClickEvent = (eventName: GifGAEvent) => {
    sendGAEvent({
        event: GA_EVENT_NAMES.GENERIC,
        event_name: eventName,
    });
};

export const sendChannelClickEvent = (eventName: ChannelGAEvent) => {
    sendGAEvent({
        event: GA_EVENT_NAMES.GENERIC,
        event_name: eventName,
    });
};

/**
 * Tracks various Photo page events, such as button clicks.
 */
export const sendPhotoClickEvent = (eventName: PhotoGAEvent) => {
    sendGAEvent({
        event: GA_EVENT_NAMES.GENERIC,
        event_name: eventName,
    });
};

/**
 * Sends a GA event upon page load, or login/signup events.
 * The event is sent only if the tracking ID is present in the X-TR-ID header.
 * When called with isLoggedIn=true and siteDataTrId, it initializes dataLayer with the logged-in user's tracking ID.
 */
export const reportTrackingIdToGA = (
    isLoggedIn: boolean,
    siteDataTrId: string | undefined,
    event?: TrackingIdEvents,
    headerTrId?: string
) => {
    window.dataLayer = window.dataLayer || [];

    if (!isLoggedIn && event && headerTrId) {
        // Add optional signup parameters
        if (event === 'signup') {
            sendSignupEvent(headerTrId, isAdvancedAvType(), 'regular');
        }

        if (event === 'login') {
            sendLoginEvent(headerTrId);
        }
    }

    if (isLoggedIn && siteDataTrId) {
        window.dataLayer.push({
            user_id: siteDataTrId,
        });
    }
};

/**
 * Event listener for tracking ID events. Temporary solution until the signup/login functions are migrated to TypeScript.
 * After that's done, this can be removed and the reportTrackingIdToGA function can be called directly from those functions.
 * Listens for custom 'trackingIdEvent' events and calls reportTrackingIdToGA.
 */
window.addEventListener('trackingIdEvent', ((event: CustomEvent<{ event: TrackingIdEvents; headerTrId: string }>) => {
    const globalData = appStore.getGlobalData();
    const isLoggedIn = globalData.user?.isLoggedIn ?? false;
    const siteDataTrId = globalData.user?.trId;
    reportTrackingIdToGA(isLoggedIn, siteDataTrId, event?.detail?.event, event?.detail?.headerTrId);
}) as EventListener);
